export interface ExportToPdfOptions {
  filename: string;
  element: HTMLElement;
  onStart?: () => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Export an HTML element to PDF using the browser's native print functionality.
 * This approach fully supports modern CSS including oklch colors.
 */
export async function exportToPdf(options: ExportToPdfOptions): Promise<void> {
  const { filename, element, onStart, onComplete, onError } = options;

  try {
    onStart?.();

    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');

    if (!printWindow) {
      throw new Error(
        'Failed to open print window. Please allow popups for this site.'
      );
    }

    // Get all stylesheets
    const stylesheets = Array.from(document.styleSheets);
    let cssText = '';

    for (const sheet of stylesheets) {
      try {
        if (sheet.cssRules) {
          for (const rule of Array.from(sheet.cssRules)) {
            cssText += rule.cssText + '\n';
          }
        }
      } catch {
        // Skip cross-origin stylesheets
      }
    }

    // Clone the element
    const clone = element.cloneNode(true) as HTMLElement;

    // Write the print document
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${filename.replace('.pdf', '')}</title>
          <style>
            ${cssText}

            @media print {
              body {
                margin: 0;
                padding: 20px;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }

            body {
              font-family: Roobert, Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              background: white;
              margin: 0;
              padding: 20px;
            }
          </style>
        </head>
        <body>
          ${clone.outerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();

    // Wait for content to load
    await new Promise<void>(resolve => {
      printWindow.onload = () => resolve();
      // Fallback timeout in case onload doesn't fire
      setTimeout(resolve, 500);
    });

    // Trigger print dialog (user can save as PDF)
    printWindow.print();

    // Close the window after a delay to allow print dialog to appear
    setTimeout(() => {
      printWindow.close();
      onComplete?.();
    }, 1000);
  } catch (error) {
    onError?.(
      error instanceof Error ? error : new Error('Failed to export PDF')
    );
  }
}

export function generatePdfFilename(title: string): string {
  // Sanitize title: remove special characters, replace spaces with underscores
  const sanitizedTitle = title
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50); // Limit length

  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  return `${sanitizedTitle}_${date}.pdf`;
}
